<!-- PDF preview overlay -->
<div class="preview-overlay" *ngIf="showPreview">
  <div class="preview-container">
    <div class="preview-header">
      <h3>PO Preview</h3>
      <div class="d-flex gap-2">
        <button class="btn btn-secondary" (click)="printPdf()">
          <i class="bi bi-printer"></i> Print
        </button>
        <button class="btn btn-secondary" (click)="downloadPdf()">
          <i class="bi bi-download"></i> Download
        </button>
        <button class="btn btnprimary" (click)="closePreview()">Done</button>
      </div>
    </div>
    <div class="preview-body">
      <div *ngIf="isLoadingPreview" class="loading-spinner">Loading PDF...</div>
      <embed *ngIf="previewUrl" [src]="previewUrl" type="application/pdf" class="pdf-viewer">
    </div>
  </div>
</div>

<div [class.blurred]="showPreview">
  <div class="container-fluid py-3">
    <div class="content-info po-page">

      <!-- ── Sticky document header ─────────────────────────────────────── -->
      <div class="zf-doc-head">
        <div class="zf-doc-left">
          <button type="button" (click)="cancel()" class="zf-doc-back">
            <i class="bi bi-arrow-left"></i> Purchase Orders
          </button>
          <div class="zf-doc-title-row">
            <h3 class="zf-doc-title">
              {{ mode === 'view' ? 'Purchase Order' : (mode === 'edit' ? 'Edit Purchase Order' : 'Create Purchase Order') }}
            </h3>
            @if (poForm.value.poNumber) {
            <span class="zf-doc-no">{{ poForm.value.poNumber }}</span>
            }
            @if (mode === 'create') {
            <span class="zf-status warn dot">Draft</span>
            } @else if (mode === 'edit') {
            <span class="zf-status info dot">Editing</span>
            } @else {
            <span class="zf-status dot">Read only</span>
            }
          </div>
        </div>

        @if (!isViewMode) {
        <div class="zf-doc-actions">
          <div class="zf-seg" role="radiogroup" aria-label="Cost price basis"
            title="How the vendor invoices you. Auto-fills from the vendor's saved default.">
            <button type="button" class="zf-seg-opt" [class.on]="!costPriceIncludesGst"
              (click)="setCostBasis(false)" title="Vendor quotes basic price; GST added on top">
              Basic + GST
            </button>
            <button type="button" class="zf-seg-opt" [class.on]="costPriceIncludesGst"
              (click)="setCostBasis(true)" title="Vendor quotes inclusive cost; GST back-calculated">
              Incl. GST
            </button>
          </div>
          <span class="zf-vsep"></span>
          <button type="button" class="btn btn-secondary" (click)="openBulk()" [disabled]="!selectedVendor"
            [title]="selectedVendor ? 'Download the template, fill it, upload it back' : 'Select a vendor first'">
            <i class="bi bi-upload"></i> Bulk items
          </button>
          <button type="button" class="btn btn-secondary" (click)="submitPo('DRAFT')" [disabled]="!poItems.length">
            Save draft
          </button>
          @if (mode === 'edit') {
          <button type="button" class="btn zf-btn-hero" (click)="submitPo()" [disabled]="!poItems.length">Post PO</button>
          } @else {
          <button type="button" class="btn zf-btn-hero" (click)="submitPo()" [disabled]="!poItems.length">Submit PO</button>
          }
        </div>
        }
      </div>

      <!-- ── Order header ───────────────────────────────────────────────── -->
      <div class="zf-doc-section">
        <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
          <span class="zf-section-title">Order header</span>
          <div class="zf-sum-chips">
            <span class="zf-sum-chip po-store-chip">
              <span class="k">Store</span>
              <app-warehouse-select [initialLocationId]="documentLocationId"
                (selectionChange)="onWarehouseChange($event)"></app-warehouse-select>
            </span>
            <span class="zf-sum-chip" [class.muted]="!selectedVendorName">
              <span class="k">Vendor</span>{{ selectedVendorName || 'Not selected' }}
            </span>
            @if (selectedManufacturerLabel) {
            <span class="zf-sum-chip"><span class="k">Mfr</span>{{ selectedManufacturerLabel }}</span>
            }
            @if (categoryLabel) {
            <span class="zf-sum-chip"><span class="k">Cat</span>{{ categoryLabel }}</span>
            }
            <span class="zf-sum-chip">
              <span class="k">Delivery</span>
              <span class="mono">{{ (poForm.value.deliveryDate | date:'dd MMM yyyy') || '—' }}</span>
            </span>
            @if (selectedIndents.length) {
            <span class="zf-sum-chip primary">{{ selectedIndents.length }} {{ selectedIndents.length === 1 ? 'indent' : 'indents' }}</span>
            }
          </div>
          <span class="zf-section-toggle">
            {{ headerOpen ? 'Collapse header' : 'Edit header' }}
            <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
          </span>
        </div>

        @if (headerOpen) {
        <form [formGroup]="poForm" class="zf-section-body zf-stack">

          <div class="zf-fgrid" style="--cols: minmax(0,1.35fr) minmax(0,1.15fr) minmax(0,1fr) 148px 148px">
            <div class="zf-field">
              <span class="zf-flabel">Vendor <span class="req">*</span></span>
              <app-search-select [items]="vendors" bindLabel="name" bindValue="id" entity="vendor"
                [(ngModel)]="selectedVendor" [ngModelOptions]="{ standalone: true }"
                placeholder="Select vendor" (change)="onVendorChange()" [disabled]="isViewMode">
              </app-search-select>
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Manufacturer
                @if (manufacturers.length) { <span class="meta">· {{ manufacturers.length }} available</span> }
              </span>
              <ng-select [items]="manufacturers" bindLabel="name" bindValue="id" [multiple]="true" [searchable]="true"
                [(ngModel)]="selectedManufacturers" [ngModelOptions]="{ standalone: true }"
                placeholder="All manufacturers" (change)="onManufacturersChange()" [disabled]="isViewMode">
              </ng-select>
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Brand
                @if (brands.length) { <span class="meta">· {{ brands.length }} available</span> }
              </span>
              <ng-select [items]="brands" bindLabel="name" bindValue="id" [multiple]="true" [searchable]="true"
                [(ngModel)]="selectedBrands" [ngModelOptions]="{ standalone: true }" placeholder="All brands"
                [disabled]="isViewMode">
              </ng-select>
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Delivery date <span class="req">*</span></span>
              <input type="date" class="form-control" formControlName="deliveryDate" [readonly]="isViewMode" />
              @if (poForm.get('deliveryDate')?.errors?.['required'] && poForm.get('deliveryDate')?.touched) {
              <div class="invalid-feedback">Delivery date is required</div>
              }
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Expiry date</span>
              <input type="date" class="form-control" formControlName="expiryDate" [readonly]="isViewMode" />
              @if (poForm.get('expiryDate')?.errors?.['required'] && poForm.get('expiryDate')?.touched) {
              <div class="invalid-feedback">Expiry date is required</div>
              }
            </div>
          </div>

          <div class="zf-hr"></div>

          <div class="zf-fgrid" style="--cols: minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 170px 170px">
            <div class="zf-field">
              <span class="zf-flabel">Department</span>
              <ng-select [items]="departments" bindLabel="name" bindValue="id" [multiple]="true" [searchable]="true"
                [(ngModel)]="selectedDepartment" [ngModelOptions]="{ standalone: true }" placeholder="All departments"
                (change)="onDepartmentChange()" [disabled]="isViewMode">
              </ng-select>
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Category
                @if (categories.length) { <span class="meta">· {{ categories.length }} available</span> }
              </span>
              <ng-select [items]="categories" bindLabel="name" bindValue="id" [searchable]="true"
                [(ngModel)]="selectedCategory" [ngModelOptions]="{ standalone: true }" placeholder="All categories"
                (change)="onCategoryChange()" [disabled]="isViewMode">
              </ng-select>
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Sub category
                @if (subCategories.length) { <span class="meta">· {{ subCategories.length }} available</span> }
              </span>
              <ng-select [items]="subCategories" bindLabel="name" bindValue="id" [multiple]="true" [searchable]="true"
                [(ngModel)]="selectedSubCategories" [ngModelOptions]="{ standalone: true }"
                placeholder="All sub categories" [disabled]="isViewMode">
              </ng-select>
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Scheme disc.</span>
              <div class="zf-affix" [class.disabled]="isViewMode">
                <input type="number" min="0" formControlName="schemeDiscountAmount" (change)="updateSummary()"
                  [readOnly]="isViewMode" placeholder="0" />
                <span class="zf-affix-k on">₹</span>
              </div>
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Fixed disc.</span>
              <div class="zf-affix" [class.disabled]="isViewMode">
                <input type="number" min="0" formControlName="fixedDiscountAmount" (change)="updateSummary()"
                  [readOnly]="isViewMode" placeholder="0" />
                <span class="zf-affix-k on">₹</span>
              </div>
            </div>
          </div>

          <div class="zf-hr"></div>

          <div class="zf-fgrid" style="--cols: 148px minmax(0,1fr) 100px 132px">
            <div class="zf-field">
              <span class="zf-flabel">Indent date</span>
              <input type="date" class="form-control" [(ngModel)]="fromDate" [ngModelOptions]="{ standalone: true }"
                (change)="onFromDateChange()" [readOnly]="isViewMode" />
            </div>

            <div class="zf-field">
              <span class="zf-flabel">Indents
                @if (indentOptions.length) { <span class="meta">· {{ indentOptions.length }} raised{{ fromDate ? ' on ' + (fromDate | date:'dd MMM') : '' }}</span> }
              </span>
              <ng-select [items]="indentOptions" bindLabel="label" bindValue="id" [multiple]="true" [searchable]="true"
                [(ngModel)]="selectedIndents" [ngModelOptions]="{ standalone: true }"
                [placeholder]="indentOptions.length ? 'Search & add indents…' : 'Pick an indent date to list indents'"
                [disabled]="isViewMode || !indentOptions.length">
              </ng-select>
            </div>

            <button class="btn btn-secondary" type="button" (click)="selectAllIndents()"
              [disabled]="isViewMode || !indentOptions.length">
              Select all
            </button>
            <button class="btn zf-btn-dark" type="button" (click)="loadFilteredProducts()" [disabled]="isViewMode">
              Load products
            </button>
          </div>
        </form>
        }
      </div>

      <!-- ── Products ───────────────────────────────────────────────────── -->
      <div class="zf-doc-section zf-lines-card">
        <div class="zf-lines-head">
          <h2>Products</h2>
          <span class="zf-count">{{ lineCountLabel }}</span>
          <span class="zf-units">{{ totalReceivedQuantity | number:'1.0-2' }} units</span>
          <div class="zf-pills">
            <button type="button" class="zf-pill" [class.on]="lineFilter === 'all'" (click)="lineFilter = 'all'">
              All<span class="n">{{ poItems.length }}</span>
            </button>
            <button type="button" class="zf-pill" [class.on]="lineFilter === 'indent'" (click)="lineFilter = 'indent'">
              From indents<span class="n">{{ indentLineCount }}</span>
            </button>
            <button type="button" class="zf-pill" [class.on]="lineFilter === 'manual'" (click)="lineFilter = 'manual'">
              Manually added<span class="n">{{ manualLineCount }}</span>
            </button>
            @if (lineFilter !== 'all' || lineQuery) {
            <button type="button" class="zf-pill clear" (click)="clearLineFilters()">✕ Clear filters</button>
            }
          </div>

          <div class="zf-lines-tools">
            <label class="zf-search" style="width:150px">
              <i class="bi bi-search"></i>
              <input [(ngModel)]="lineQuery" [ngModelOptions]="{ standalone: true }" placeholder="Filter these lines" />
            </label>

            @if (!isViewMode) {
            <div class="zf-tool" style="width:140px">
              <span class="zf-tool-k mono">SKU</span>
              <ng-select [items]="searchResults" bindLabel="sku" [typeahead]="productCodeSearch$"
                [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }"
                (change)="onSearchResultSelected($event)" [loading]="isCodeSearching" placeholder="Scan to add"
                [virtualScroll]="true" [clearable]="true">
                <ng-template ng-option-tmp let-item="item">
                  <div class="ss-row">
                    <span class="ss-row__primary">{{ item.sku }}</span>
                    <span class="ss-row__secondary">{{ item.longDescription }}</span>
                  </div>
                </ng-template>
              </ng-select>
            </div>

            <div class="zf-tool" style="width:140px">
              <span class="zf-tool-k mono">EAN</span>
              <ng-select [items]="eanSearchResults" bindLabel="matchedEan" [typeahead]="productEanSearch$"
                [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }"
                (change)="onSearchResultSelected($event)" [loading]="isEanSearching" placeholder="Scan EAN"
                [virtualScroll]="true" [clearable]="true">
                <ng-template ng-label-tmp let-item="item">{{ item.matchedEan || item.ean || item.sku }}</ng-template>
                <ng-template ng-option-tmp let-item="item">
                  <div class="ss-row">
                    <span class="ss-row__primary">{{ item.matchedEan }}</span>
                    <span class="ss-row__secondary">{{ item.sku }} · {{ item.longDescription }}</span>
                  </div>
                </ng-template>
              </ng-select>
            </div>

            <div class="zf-tool" style="width:230px">
              <span class="zf-tool-k"><i class="bi bi-plus-lg"></i></span>
              <ng-select [items]="products" bindLabel="longDescription" [typeahead]="productSearch$"
                [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }" (change)="onProductSelected($event)"
                [loading]="isSearching" placeholder="Search & add product" [virtualScroll]="true"
                [searchFn]="customSearchFn">
                <ng-template ng-label-tmp let-item="item">{{ item.longDescription || item.skuCode }}</ng-template>
                <ng-template ng-option-tmp let-item="item">
                  <div class="ss-row">
                    <span class="ss-row__primary">{{ item.longDescription || item.skuCode }}</span>
                    <span class="ss-row__secondary">{{ item.sku || item.skuCode }}</span>
                  </div>
                </ng-template>
              </ng-select>
            </div>

            <button type="button" class="btn btn-secondary" (click)="clearLines()" [disabled]="!poItems.length">
              Clear lines
            </button>
            }
          </div>
        </div>

        <!-- Add-line strip: appears once a product is picked -->
        @if (selectedProduct && !editingItem && !isViewMode) {
        <div class="zf-add-strip">
          <div class="zf-add-prod">
            <span class="k">Adding</span>
            <span class="n" [title]="selectedProduct.longDescription || selectedProduct.sku">
              {{ selectedProduct.longDescription || selectedProduct.description || selectedProduct.sku }}
            </span>
            <span class="s">
              SKU {{ selectedVariant?.sku || selectedProduct.sku || selectedProduct.skuCode }}
              · GST {{ selectedProduct?.purchaseGstRate?.value || 0 }}%
            </span>
          </div>
          <div class="zf-field w-90">
            <span class="zf-flabel">Qty</span>
            <input id="quantityInput" #quantityInput type="number" class="form-control" [(ngModel)]="selectedQuantity"
              appQtyInput [allowDecimal]="allowsDecimal(selectedProduct)" [ngModelOptions]="{ standalone: true }"
              (keydown.enter)="confirmAddToTable()" />
          </div>
          <div class="zf-field">
            <span class="zf-flabel">Cost price</span>
            <select class="form-select" [(ngModel)]="enteredCostPrice" [ngModelOptions]="{ standalone: true }"
              (keydown.enter)="confirmAddToTable()">
              @for (price of allCostPrices; track price) {
              <option [value]="price">{{ price }}</option>
              }
            </select>
          </div>
          <div class="zf-field">
            <span class="zf-flabel">MRP</span>
            <select class="form-select" [(ngModel)]="MRP" [ngModelOptions]="{ standalone: true }"
              (keydown.enter)="confirmAddToTable()">
              @for (mrp of allMRPs; track mrp) {
              <option [value]="mrp">{{ mrp }}</option>
              }
            </select>
          </div>
          <button class="btn btnprimary" type="button" (click)="confirmAddToTable()">Add line</button>
          <button type="button" class="btn btn-secondary" (click)="openNewPricingForm()">
            <i class="bi bi-plus-circle"></i> New pricing
          </button>
          <button type="button" class="btn zf-btn-ghost" (click)="clearSelectedProduct()" title="Cancel">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        }

        @if (selectedVendorName && !isViewMode) {
          @if (!openVendorScope) {
          <div class="zf-scoped">
            <span class="k">Scoped</span>
            <span>Only SKUs mapped to <strong>{{ selectedVendorName }}</strong> can be added</span>
            <span class="hint">Rows locked left · scroll right for tax &amp; totals</span>
          </div>
          } @else {
          <div class="zf-scoped po-open-scope" [class.has-unmapped]="unmappedLineCount > 0">
            <span class="k">{{ unmappedLineCount > 0 ? 'Unmapped' : 'Open' }}</span>
            @if (unmappedLineCount > 0) {
            <span>
              <strong>{{ unmappedLineCount }}</strong> line{{ unmappedLineCount === 1 ? '' : 's' }} not mapped to
              <strong>{{ selectedVendorName }}</strong> — {{ vendorSkuMappingMode === 'REQUIRED' ? 'map or remove them before submitting' : 'you will be asked to map them on submit' }}
            </span>
            <button type="button" class="zf-link" (click)="reviewVendorSkuMapping()">Review mapping</button>
            } @else {
            <span>
              Any SKU can be added. Lines not mapped to <strong>{{ selectedVendorName }}</strong> {{ openScopeHint }}.
            </span>
            }
            @if (mappingCheckPending) { <span class="po-map-check"><i class="bi bi-arrow-repeat"></i> checking</span> }
            <span class="hint">Rows locked left · scroll right for tax &amp; totals</span>
          </div>
          }
        }

        @if (uploadErrors.length) {
        <div class="zf-issues">
          <div class="zf-issues-head">
            <i class="bi bi-exclamation-triangle-fill"></i>
            Upload errors — {{ uploadErrors.length }} row{{ uploadErrors.length === 1 ? '' : 's' }} skipped
            <button type="button" class="zf-link ms-auto" (click)="uploadErrors = []">Dismiss</button>
          </div>
          <div style="max-height: 180px; overflow-y: auto;">
            <table class="table mb-0">
              <thead><tr><th style="width:70px">Row</th><th style="width:140px">Code</th><th>Error</th></tr></thead>
              <tbody>
                @for (err of uploadErrors; track $index) {
                <tr>
                  <td class="zf-mono">{{ err.row }}</td>
                  <td><code>{{ err.code }}</code></td>
                  <td>{{ err.error }}</td>
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        }

        @if (poItems.length) {
        <div class="zf-sheet-wrap">
          <table class="zf-sheet">
            <thead>
              <tr>
                <th class="st" style="--l:0; width:42px">S.No</th>
                <th class="st" style="--l:42px; width:72px">SKU</th>
                <th class="st st-end" style="--l:114px">Product name</th>
                <th class="num" style="width:72px">Qty</th>
                <th class="num" style="width:82px">Indent qty</th>
                <th class="num" style="width:72px">Free qty</th>
                <th class="num" style="width:68px">Disc %</th>
                <th class="num" style="width:92px">Basic cost</th>
                <th style="width:116px">Cost price</th>
                <th class="num" style="width:66px">MRP</th>
                <th class="num" style="width:58px">GST %</th>
                <th class="num" style="width:76px">GST ₹</th>
                <th class="num" style="width:58px">Cess %</th>
                <th class="num" style="width:70px">Cess ₹</th>
                <th class="num" style="width:104px">Basic total</th>
                <th class="num" style="width:108px">Cost total</th>
                <th class="ctr" style="width:52px">Action</th>
              </tr>
            </thead>
            <tbody>
              @for (row of visibleItems; track row.item; let i = $index) {
              <tr>
                <td class="st zf-idx" style="--l:0">{{ row.index + 1 }}</td>
                <td class="st" style="--l:42px">
                  <span class="zf-sku" (click)="!isViewMode && openNewPricingForm(row.item)"
                    [title]="isViewMode ? '' : 'Change or add pricing'">
                    {{ row.item?.sku || row.item?.skuCode }}
                  </span>
                  @if (row.item?.vendorMapped === false) {
                  <span class="po-unmapped"
                    [title]="'Not mapped to ' + selectedVendorName + (row.item?.vendorNames?.length ? ' · supplied by ' + row.item.vendorNames.join(', ') : '')">
                    Unmapped
                  </span>
                  }
                </td>
                <td class="st st-end zf-name" style="--l:114px">
                  <span class="n" [title]="row.item?.variantName">{{ row.item?.variantName }}</span>
                  <span class="s">{{ row.item.indentQuantity > 0 ? 'FROM INDENT' : 'MANUAL' }}</span>
                </td>
                <td class="num">
                  <input type="number" class="zf-cell-in" [id]="'po-qty-' + row.index"
                    appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                    [ngModel]="row.item.quantity" min="0" max="9999"
                    (ngModelChange)="row.item.quantity = $event; onQuantityChange(row.item)"
                    [ngModelOptions]="{ standalone: true }" [readOnly]="isViewMode" />
                </td>
                <td class="num">
                  <input type="number" class="zf-cell-in" appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                    [ngModel]="row.item.indentQuantity" min="0" max="9999"
                    (ngModelChange)="row.item.indentQuantity = $event; onQuantityChange(row.item)"
                    [ngModelOptions]="{ standalone: true }" [readOnly]="isViewMode || row.item.indentQuantity > 0" />
                </td>
                <td class="num">
                  <input type="number" class="zf-cell-in w-sm" appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                    [ngModel]="row.item.freeQuantity" min="0" max="9999"
                    (ngModelChange)="row.item.freeQuantity = $event; onQuantityChange(row.item)"
                    [ngModelOptions]="{ standalone: true }" [readOnly]="isViewMode" />
                </td>
                <td class="num">
                  <input type="number" class="zf-cell-in w-sm" [ngModel]="row.item.discountPercent" min="0" max="100"
                    (ngModelChange)="row.item.discountPercent = $event; onDiscountChange(row.item)"
                    [ngModelOptions]="{ standalone: true }" [readOnly]="isViewMode" />
                </td>
                <td class="num">{{ row.item?.actualCostPrice | number:'1.2-4' }}</td>
                <td>
                  <select class="zf-price-sel" [(ngModel)]="row.item.costPrice" [ngModelOptions]="{ standalone: true }"
                    (change)="onQuantityChange(row.item)" [disabled]="isViewMode">
                    @for (pricingItem of row.item.pricing; track pricingItem) {
                    <option [value]="pricingItem.costPrice">{{ pricingItem.costPrice }}</option>
                    }
                  </select>
                </td>
                <td class="num ink3">{{ row.item?.mrp }}</td>
                <td class="num ink3">{{ row.item?.gstPercent }}%</td>
                <td class="num ink3">{{ row.item?.gstAmount | number:'1.2-2' }}</td>
                <td class="num ink3">{{ row.item?.cessRate || 0 }}%</td>
                <td class="num ink3">{{ (row.item?.cessAmount || 0) | number:'1.2-2' }}</td>
                <td class="num">{{ (row.item?.basicCostTotal || 0) | number:'1.2-2' }}</td>
                <td class="num strong">{{ row.item?.total | number:'1.2-2' }}</td>
                <td class="ctr">
                  <button type="button" class="zf-del" title="Delete line" (click)="removeLine(row.item)"
                    [disabled]="isViewMode">
                    <i class="bi bi-trash3"></i>
                  </button>
                </td>
              </tr>
              }
              @if (!visibleItems.length) {
              <tr>
                <td colspan="17" class="zf-sheet-empty">
                  <div>
                    <span class="t">No lines match this filter</span>
                    <span class="s">Clear the filters to see all {{ poItems.length }} lines.</span>
                  </div>
                </td>
              </tr>
              }
            </tbody>
            <tfoot>
              <tr>
                <td class="st" colspan="3" style="--l:0"><span class="k">TOTAL</span>{{ lineCountLabel }}</td>
                <td class="num">{{ totalOrderedQty | number:'1.0-2' }}</td>
                <td class="num dim">{{ totalIndentQty | number:'1.0-2' }}</td>
                <td class="num dim">{{ totalFreeQty | number:'1.0-2' }}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td class="num dim">{{ summary.totalGstAmount | number:'1.2-2' }}</td>
                <td></td>
                <td class="num dim">{{ summary.totalCessAmount | number:'1.2-2' }}</td>
                <td class="num dim">{{ summary.totalBasicCost | number:'1.2-2' }}</td>
                <td class="num strong">{{ grossLineTotal | number:'1.2-2' }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        } @else {
        <div class="zf-sheet-blank">
          <span class="t">No line items on this order</span>
          <span class="s">
            @if (isViewMode) { This order has no lines. }
            @else { Load products from the selected indents, scan a SKU, or search to add a product. }
          </span>
        </div>
        }
      </div>

      <!-- ── Order value strip ──────────────────────────────────────────── -->
      <div class="zf-order-strip">
        <div class="zf-strip-info">
          <div class="zf-strip-line">
            <span class="dot" [class.ok]="poItems.length"></span>
            {{ lineCountLabel }} · {{ totalReceivedQuantity | number:'1.0-2' }} units
          </div>
          <div class="zf-strip-sub">
            @if (!isViewMode) {
            <button type="button" class="zf-link" (click)="openBulk()" [disabled]="!selectedVendor">
              Upload line items from template
            </button>
            <span class="sep">·</span>
            }
            @if (!poItems.length) {
            <span>Add at least one product to save a draft or submit.</span>
            } @else {
            <span>
              Scheme ₹{{ (poForm.value.schemeDiscountAmount || 0) | number:'1.0-2' }}
              and fixed ₹{{ (poForm.value.fixedDiscountAmount || 0) | number:'1.0-2' }} discounts applied
              · totals stay pinned to the grid
            </span>
            }
          </div>
        </div>
        <div class="zf-strip-kpis">
          <div class="zf-kpi green">
            <span class="k">Discount</span>
            <span class="v">− ₹{{ totalDiscount | number:'1.2-2' }}</span>
          </div>
          <div class="zf-kpi">
            <span class="k">Taxable</span>
            <span class="v">₹{{ summary.totalBasicCost | number:'1.2-2' }}</span>
          </div>
          <div class="zf-kpi">
            <span class="k">GST</span>
            <span class="v">₹{{ summary.totalGstAmount | number:'1.2-2' }}</span>
          </div>
          @if (summary.totalCessAmount) {
          <div class="zf-kpi">
            <span class="k">Cess</span>
            <span class="v">₹{{ summary.totalCessAmount | number:'1.2-2' }}</span>
          </div>
          }
          <div class="zf-kpi dark">
            <span class="k">Order value · {{ costPriceIncludesGst ? 'Incl. GST' : 'Basic + GST' }}</span>
            <span class="v">₹{{ summary.totalPoValue | number:'1.2-2' }}</span>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- ── New pricing dialog ─────────────────────────────────────────────── -->
  @if (isPricingFormVisible && !isViewMode) {
  <div class="zf-overlay" (click)="closePricingForm()">
    <div class="zf-dialog" (click)="$event.stopPropagation()">
      <div class="zf-dialog-head">
        <h3>Add new pricing</h3>
        <span class="zf-sum-chip primary"><span class="k">SKU</span>{{ selectedProduct?.sku || selectedVariant?.sku || 'N/A' }}</span>
        <span class="zf-dialog-sub" [title]="selectedProduct?.longDescription || selectedProduct?.description">
          {{ selectedProduct?.longDescription || selectedProduct?.description }}
        </span>
        <button type="button" class="zf-x" aria-label="Close" (click)="closePricingForm()">✕</button>
      </div>

      <form #poPricingForm="ngForm" class="zf-dialog-body">
        @if (newPricingValidationError) {
        <div class="zf-alert danger">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <span><strong>Pricing validation:</strong> {{ newPricingValidationError }}</span>
        </div>
        }
        @if (!newPricingValidationError && isZeroMarginPricing) {
        <div class="zf-alert warn">
          <i class="bi bi-info-circle-fill"></i>
          <span><strong>Zero margin:</strong> selling price equals cost price — this item will sell at cost. You can still save it.</span>
        </div>
        }

        @if (editingItem?.pricing?.length) {
        <div class="d-flex flex-column gap-2">
          <span class="zf-dialog-k">Known prices — pick one to apply</span>
          <div class="zf-price-opts">
            @for (p of editingItem.pricing; track $index) {
            <button type="button" class="zf-price-opt" [class.on]="+editingItem.costPrice === +p.costPrice"
              (click)="applyKnownPrice(p)">
              <span class="v">₹{{ p.costPrice | number:'1.2-2' }}</span>
              <span class="m">MRP ₹{{ p.mrp }}{{ p.sellingPrice ? ' · SP ₹' + p.sellingPrice : '' }}</span>
            </button>
            }
          </div>
        </div>
        <div class="zf-hr"></div>
        }

        <div class="d-flex flex-column gap-2">
          <label class="form-check">
            <input class="form-check-input" type="checkbox" id="poUseMarginForCp" name="useMarginForCp"
              [(ngModel)]="useMarginForCp" (ngModelChange)="onMarginSettingChange()" />
            <span class="form-check-label">Calculate cost price from MRP (margin %)</span>
          </label>
          @if (useMarginForCp) {
          <div class="zf-field" style="max-width: 200px">
            <span class="zf-flabel">Margin %</span>
            <div class="zf-affix">
              <input type="number" step="0.01" min="0" max="99.99" [(ngModel)]="pricingMarginPercent"
                name="pricingMarginPercent" (ngModelChange)="onMarginSettingChange()" />
              <span class="zf-affix-k">%</span>
            </div>
          </div>
          }
        </div>

        <div class="zf-dialog-grid">
          <label class="zf-field">
            <span class="zf-flabel">Base price</span>
            <div class="zf-affix prefix">
              <span class="zf-affix-k">₹</span>
              <input type="number" step="0.0001" placeholder="0.00" [(ngModel)]="newPricing.basePrice" name="basePrice"
                required (ngModelChange)="calculateNewPricing('base'); validatePricing()" />
            </div>
          </label>
          <label class="zf-field">
            <span class="zf-flabel">Cost price <span class="meta">· incl. GST</span></span>
            <div class="zf-affix prefix">
              <span class="zf-affix-k">₹</span>
              <input type="number" step="0.0001" placeholder="0.00" [(ngModel)]="newPricing.costPrice" name="costPrice"
                required (ngModelChange)="calculateNewPricing('cost'); validatePricing()" />
            </div>
          </label>
          <label class="zf-field">
            <span class="zf-flabel">Selling price</span>
            <div class="zf-affix prefix">
              <span class="zf-affix-k">₹</span>
              <input type="number" step="0.01" placeholder="0.00" [(ngModel)]="newPricing.sellingPrice"
                name="sellingPrice" required (ngModelChange)="calculateNewPricing('sellingPrice'); validatePricing()" />
            </div>
            @if (customerMarginPercent !== null) {
            <small class="zf-field-hint" [class.text-danger]="customerMarginPercent < 0">
              Profit margin <strong>{{ customerMarginPercent | number: '1.2-2' }}%</strong>
            </small>
            }
          </label>
          <label class="zf-field">
            <span class="zf-flabel">MRP</span>
            <div class="zf-affix prefix">
              <span class="zf-affix-k">₹</span>
              <input type="number" step="0.01" placeholder="0.00" [(ngModel)]="newPricing.mrp" name="mrp" required
                (ngModelChange)="calculateNewPricing('mrp'); validatePricing()" />
            </div>
            @if (vendorMarginPercent !== null) {
            <small class="zf-field-hint" [class.text-danger]="vendorMarginPercent < 0">
              Vendor margin <strong>{{ vendorMarginPercent | number: '1.2-2' }}%</strong>
            </small>
            }
          </label>
        </div>
      </form>

      <div class="zf-dialog-foot">
        <span class="note">Saved pricing applies to this line and is logged against the vendor.</span>
        <button type="button" class="btn btn-secondary" (click)="closePricingForm()">Cancel</button>
        <button type="button" class="btn btnprimary" (click)="saveNewPricing()"
          [disabled]="!poPricingForm.valid || getPricingValidationError() !== null">
          Save pricing
        </button>
      </div>
    </div>
  </div>
  }

  <!-- ── Bulk line items dialog ─────────────────────────────────────────── -->
  @if (bulkOpen) {
  <div class="zf-overlay" (click)="closeBulk()">
    <div class="zf-dialog w-600" (click)="$event.stopPropagation()">
      <div class="zf-dialog-head">
        <h3>Bulk line items</h3>
        <span class="zf-dialog-sub">Download the format, fill it, upload it back</span>
        <button type="button" class="zf-x" aria-label="Close" (click)="closeBulk()">✕</button>
      </div>

      <div class="zf-dialog-body">
        <div class="zf-file-card">
          <div class="ic"><i class="bi bi-download"></i></div>
          <div class="min-w-0">
            <div class="t">Line item template</div>
            <div class="s">XLSX · pre-filled with {{ selectedVendorName || 'this vendor' }}'s SKUs · quantity, free qty, discount</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" (click)="downloadTemplate()">Download</button>
        </div>

        <div class="zf-dropzone" [class.over]="dragOver" (dragover)="onDragOver($event)" (dragleave)="dragOver = false"
          (drop)="onBulkDrop($event)">
          <i class="bi bi-upload"></i>
          <div>
            <div class="t">Drop the filled sheet here</div>
            <div class="s">XLSX or XLS · one file at a time</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" (click)="bulkFileInput.click()">Browse files</button>
          <input #bulkFileInput type="file" accept=".xlsx,.xls" hidden (change)="onBulkFilePicked($event)" />
        </div>

        @if (bulkFile) {
        <div class="zf-file-row">
          <span class="dot"></span>
          <span class="n">{{ bulkFile.name }}</span>
          <span class="s">{{ (bulkFile.size / 1024) | number:'1.0-0' }} KB · ready to import</span>
          <button type="button" class="rm" (click)="bulkFile = null">Remove</button>
        </div>
        }
      </div>

      <div class="zf-dialog-foot">
        <span class="note">Existing lines are kept; uploaded rows are appended. Same SKU at the same price adds to its quantity.</span>
        <button type="button" class="btn btn-secondary" (click)="closeBulk()">Cancel</button>
        <button type="button" class="btn btnprimary" (click)="importBulk()" [disabled]="!bulkFile || isUploading">
          @if (isUploading) { <i class="bi bi-hourglass-split"></i> Importing… } @else { Import lines }
        </button>
      </div>
    </div>
  </div>
  }

  <!-- ── Vendor ↔ SKU mapping dialog ─────────────────────────────────────── -->
  @if (mappingDialog; as d) {
  <div class="zf-overlay" (click)="cancelMappingDialog()">
    <div class="zf-dialog po-map-dialog" (click)="$event.stopPropagation()">
      <div class="zf-dialog-head">
        <h3>{{ d.mode === 'STRICT' ? 'SKUs not mapped to vendor' : 'Map SKUs to ' + d.vendorName }}</h3>
        <span class="zf-dialog-sub">
          {{ d.rows.length }} of {{ d.total }} line{{ d.total === 1 ? '' : 's' }} not mapped to {{ d.vendorName }}
        </span>
        <button type="button" class="zf-x" aria-label="Close" (click)="cancelMappingDialog()" [disabled]="d.busy">✕</button>
      </div>

      <div class="zf-dialog-body">
        @switch (d.mode) {
          @case ('STRICT') {
          <div class="zf-alert danger">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>
              <strong>Mapped SKUs only.</strong> These SKUs are not mapped to {{ d.vendorName }} (the mapping may have been
              removed after the lines were added). Remove them from this order, or map them first on
              <a routerLink="/product-vendor-mapping" target="_blank" rel="noopener">Products Vendor Mapping</a> and submit again.
            </span>
          </div>
          }
          @case ('REQUIRED') {
          <div class="zf-alert warn">
            <i class="bi bi-info-circle-fill"></i>
            <span>
              <strong>Mapping required.</strong> Every line must be supplied by {{ d.vendorName }} before the order can be
              {{ d.purpose === 'review' ? 'submitted' : 'posted' }}. Map each SKU to the vendor, or remove the line.
            </span>
          </div>
          }
          @default {
          <div class="zf-alert info">
            <i class="bi bi-info-circle-fill"></i>
            <span>
              <strong>Choose per SKU.</strong> <em>Map</em> adds {{ d.vendorName }} as a supplier of the SKU;
              <em>Skip</em> {{ d.purpose === 'review' ? 'leaves it as it is' : 'posts the line without a mapping' }};
              <em>Remove</em> drops the line from this order.
            </span>
          </div>
          }
        }

        @if (d.mode !== 'STRICT') {
        <div class="po-map-bulk">
          <span class="zf-dialog-k">Set all to</span>
          @for (opt of mappingDecisionOptions; track opt) {
          <button type="button" class="zf-link" (click)="setAllMappingDecisions(opt)" [disabled]="d.busy">
            {{ opt === 'map' ? 'Map' : opt === 'skip' ? 'Skip' : 'Remove' }}
          </button>
          }
          <span class="po-map-note">Mapping adds a supplier; a SKU can be supplied by several vendors and existing links are kept.</span>
        </div>
        }

        <div class="po-map-list">
          @for (row of d.rows; track row.productId) {
          <div class="po-map-row" [class.is-remove]="row.decision === 'remove'">
            <div class="po-map-sku">
              <span class="zf-sku">{{ row.sku }}</span>
              @if (row.productVariantIds.length > 1) {
              <span class="po-map-packs">{{ row.productVariantIds.length }} lines</span>
              }
            </div>
            <div class="po-map-name">
              <span class="n" [title]="row.name">{{ row.name }}</span>
              <span class="s">
                @if (row.vendors?.length) {
                Supplied by
                @for (v of row.vendors; track v.id) {
                <span class="po-map-vendor" [class.primary]="v.isPrimary" [title]="v.isPrimary ? 'Primary vendor on the product' : 'Mapped vendor'">{{ v.name }}</span>
                }
                } @else {
                No vendor mapped yet
                }
              </span>
            </div>
            @if (d.mode === 'STRICT') {
            <span class="po-map-fixed"><i class="bi bi-trash3"></i> Remove</span>
            } @else {
            <div class="po-map-seg" role="radiogroup">
              @for (opt of mappingDecisionOptions; track opt) {
              <button type="button" class="po-map-opt" [class.on]="row.decision === opt"
                [class.map]="opt === 'map'" [class.remove]="opt === 'remove'" [disabled]="d.busy"
                (click)="setMappingDecision(row, opt)" role="radio" [attr.aria-checked]="row.decision === opt">
                {{ opt === 'map' ? 'Map' : opt === 'skip' ? 'Skip' : 'Remove' }}
              </button>
              }
            </div>
            }
          </div>
          }
        </div>
      </div>

      <div class="zf-dialog-foot">
        <span class="note">
          @if (d.mode === 'STRICT') {
          {{ d.rows.length }} line{{ d.rows.length === 1 ? '' : 's' }} will be removed from this order.
          } @else {
          {{ mappingDialogCounts.map }} to map · {{ mappingDialogCounts.skip }} left unmapped · {{ mappingDialogCounts.remove }} to remove
          }
          @if (mappingDialogRemovesEverything) { · <strong>nothing would remain on the order</strong> }
        </span>
        <button type="button" class="btn btn-secondary" (click)="cancelMappingDialog()" [disabled]="d.busy">Cancel</button>
        <button type="button" class="btn btnprimary" (click)="confirmMappingDialog()"
          [disabled]="d.busy || (d.purpose === 'submit' && mappingDialogRemovesEverything)">
          @if (d.busy) { <i class="bi bi-hourglass-split"></i> Mapping… }
          @else if (d.mode === 'STRICT') { Remove {{ d.rows.length }} &amp; continue }
          @else { {{ mappingDialogConfirmLabel }} }
        </button>
      </div>
    </div>
  </div>
  }
</div>
